import React from 'react';
import _ from 'lodash';
import { getBadgeClass as helperGetBadgeClass } from 'helpers/BadgeHelper';
import { getLanguageObject } from 'helpers/GlobalHelper';
import { stringToDate, format, isAfter, now } from 'helpers/DateTimeHelper';

/**
 * Format Video Footer Info
 * @param  {Number} season  - Season Number
 * @param  {Number} episode - Episode Number
 * @returns {String}        - Formatted data as S<number>E<number>
 */
export function formatEpisodeInfo(season, episode) {
  return season && episode ? `S${season}-E${episode}` : '';
}

/**
 * Format Aired Date
 * @param  {String} originalAirDate - Original Air Date Timestamp
 * @param  {String} prefix          - prefix string
 * @returns {String}                - Formatted data as 'Aired month-day-year'
 */
export function formatAiredDate(originalAirDate = null, prefix = 'Aired ', dateFormat = 'M-D-YY') {
  if (!originalAirDate) return '';
  const formatedDate = format(stringToDate(originalAirDate), dateFormat);
  return `${prefix}${formatedDate}`;
}

/**
 * Format Video Rating
 * @param  {String} contentRating - Content Rating
 * @return {String}               - Upper cased content rating
 */
export function formatRating(contentRating) {
  return contentRating ? contentRating.toUpperCase() : '';
}

/**
 * Format Video Sub Ratings
 * @param  {Array} subRatings - Content Sub Ratings
 * @return {String}               - Upper cased content rating
 */
export function formatSubRatings(subRatings) {
  return _.upperCase(_.join(subRatings, ''));
}

/**
 * Format Video Duration
 * @param  {String} duration - Duration as 'T10M00S'
 * @return {String}               - Formated duration as '10m'
 */
export function formatDuration(duration) {
  const map = { T: '', M: 'm' };

  if (!duration) return null;

  const newDuration = duration.replace(/T|M|[0-9]{1,2}S/gi, m => {
    if (m.match(/[0-9]{1,2}S/gi)) {
      return '';
    }
    if (typeof map[m] !== 'undefined') {
      return map[m];
    }
    return m;
  });
  return newDuration;
}

/**
 * Format Seconds to Hour and Minutes
 * @param  {Number} duration - Duration as '6783.644'
 * @param  {String} [mSuffix=m] - Minutes Suffix (default 'm')
 * @param  {String} [hSuffix=h] - Hours Suffix (default 'h')
 * @param  {String} [sSuffix=s] - Seconds Suffix (default 's')
 * @return {String}               - Formated duration as '1h 53m'
 */
export function formatDurationInSeconds(duration, hSuffix = 'h', mSuffix = 'm', sSuffix = 's') {
  if (!duration) return null;
  const seconds = Math.round(duration % 60);
  const minutes = Math.round(duration / 60);
  const hours = Math.floor(minutes / 60);
  const hoursAvailable = hours > 0;
  const minutesAvailable = minutes > 0;
  const secondsAvailable = seconds > 0;
  const secondsOnly = !hoursAvailable && !minutesAvailable && secondsAvailable;
  const minutesOnly = !hoursAvailable && minutesAvailable;
  if (secondsOnly) return `${seconds}${sSuffix}`;
  if (minutesOnly) return `${minutes}${mSuffix}`;
  if (hoursAvailable) {
    const hoursText = `${hours}${hSuffix}`;
    if (minutesAvailable) {
      const minutesWithoutHours = minutes - hours * 60;
      const hoursAndMinutesAvailable = minutesWithoutHours > 0;
      if (hoursAndMinutesAvailable) return `${hoursText} ${minutesWithoutHours}${mSuffix}`;
    }
    return hoursText;
  }
  return null;
}

export function formatSeasonEpisode(seasonNumber, episodeNumber, isDotted) {
  let season = seasonNumber ? `S${seasonNumber} ` : '';
  const episode = episodeNumber ? `E${episodeNumber} ` : '';
  if (isDotted && episode) season = `${season}• `;
  return `${season}${episode}`;
}

export function formatSeasonEpisodeExtended(seasonCount, currentSeason, fullEpisodeCount) {
  const {
    global_episodeLabel: globalEpisodeLabel,
    global_episodesLabel: globalEpisodesLabel,
    global_seasonLabel: globalSeasonLabel,
    global_seasonsLabel: globalSeasonsLabel,
  } = getLanguageObject();

  const hasEpisodeLabels = globalEpisodeLabel !== undefined && globalEpisodesLabel !== undefined;
  const hasSeasonsLabels = globalSeasonLabel !== undefined && globalSeasonsLabel !== undefined;
  if (!hasEpisodeLabels || !hasSeasonsLabels) return '';

  const currentSeasonText = globalSeasonLabel;
  const seasonCountText = seasonCount === 1 ? globalSeasonLabel : globalSeasonsLabel;
  const episodeText = fullEpisodeCount === 1 ? globalEpisodeLabel : globalEpisodesLabel;

  if (!seasonCount && !currentSeason && !fullEpisodeCount) return '';
  if (seasonCount && !currentSeason && !fullEpisodeCount) return '';
  if (!seasonCount && !currentSeason && fullEpisodeCount)
    return `${fullEpisodeCount} ${episodeText}`;
  if (!seasonCount && currentSeason && !fullEpisodeCount)
    return `${currentSeasonText} ${currentSeason}`;
  if (seasonCount && !currentSeason && fullEpisodeCount)
    return `${fullEpisodeCount} ${episodeText}`;
  if (seasonCount === 1 && currentSeason && !fullEpisodeCount)
    return `${seasonCountText} ${currentSeason}`;
  if (seasonCount === 1 && currentSeason && fullEpisodeCount)
    return `${seasonCountText} ${currentSeason}, ${fullEpisodeCount} ${episodeText}`;
  if (seasonCount && currentSeason && !fullEpisodeCount) return `${seasonCount} ${seasonCountText}`;
  if (!seasonCount)
    return `${currentSeasonText} ${currentSeason}, ${fullEpisodeCount} ${episodeText}`;

  return `${seasonCount} ${seasonCountText}, ${fullEpisodeCount} ${episodeText}`;
}
export function formatMetadata(meta) {
  return _.compact(meta).join(' • ');
}

export function formatMovieMetadata({ releaseYear, contentRating, subRatings, durationInSeconds }) {
  const meta = [
    releaseYear ? `${releaseYear}` : null,
    contentRating ? `${formatRating(contentRating)} ${formatSubRatings(subRatings)}` : null,
    durationInSeconds ? `${formatDurationInSeconds(durationInSeconds)}` : null,
  ];
  return formatMetadata(meta);
}

export function formatUpcomingMetadata({
  startDate,
  contentRating,
  subRatings,
  durationInSeconds,
}) {
  const airDate = formatAiredDate(startDate, '');
  const meta = [
    airDate ? `${airDate}` : null,
    contentRating ? `${formatRating(contentRating)} ${formatSubRatings(subRatings)}` : null,
    durationInSeconds ? `${formatDurationInSeconds(durationInSeconds)}` : null,
  ];
  return formatMetadata(meta);
}

/**
 * Get Item Badge
 * @param {Object} item - Video item
 * @param {Boolean} [isHoverStateV2=false]
 * @param {Boolean} [isPosters=false]
 * @returns {String} badge - Valid badge string or null if there's no valid badge.
 */
export function getItemBadge(item, isHoverStateV2 = false, isPosters = false) {
  if (!item) return null;
  const flags = item.contentFlags && item.contentFlags instanceof Array ? item.contentFlags : [];
  if ((!isPosters && item.videoType === 'clip') || item.videoType === 'CLIP') flags.push('CLIP');
  const labels = item.contentLabel && item.contentLabel instanceof Array ? item.contentLabel : [];
  const isContinueWatching =
    item.contentLabel && _.toLower(item.contentLabel[0]) === 'continue watching';
  const options = labels.concat(flags);
  if (isHoverStateV2 && isContinueWatching && !isPosters) return 'INFO';
  return options.shift();
}

export const getBadgeClass = ({ theme, badge }) => helperGetBadgeClass(badge, theme);

export function formatTitleWithEpisodeCount({ name, fullEpisodeCount, theme }) {
  if (fullEpisodeCount && fullEpisodeCount > 0) {
    return (
      <div className={theme.titleTextContainer}>
        <div className={theme.titleText}>{name}</div>
        <div className={theme.episodeCount}>({fullEpisodeCount})</div>
      </div>
    );
  }
  return name;
}

export const isUpcomingContent = item => {
  if (!item) return false;

  const { '@type': type, startDate } = item;
  const isEpgListing = type === 'EPGListing';
  const isInFuture = isAfter(stringToDate(startDate), now());
  const isUpcoming = isEpgListing && isInFuture;
  return isUpcoming;
};
